<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="success" @click="toggle"><i class="fas fa-plus"></i> Manage exceptions</b-button>
    </slot>

    <b-modal ref="modal-container" v-model="modalShow" size="lg" :static="true" hide-footer
      :title="title" @show="onShow" @hidden="onHidden">

      <b-form-group label="Country" label-for="exceptions-country-input">
        <CountryCodeSelect id="exceptions-country-input" v-model="countryCode" />
      </b-form-group>

      <b-form-group label="Operator" label-for="exceptions-operator-input">
        <div class="d-flex align-items-start">
          <multiselect id="exceptions-operator-input" v-model="operatorModel" :options="operatorOptions"
            label="label" track-by="value" :loading="!operatorsLoaded" :multiple="false"
            placeholder="Select operator" class="flex-grow-1"
            @input="onOperatorSelected" />
          <b-button
            v-if="operatorModel"
            variant="light"
            size="sm"
            class="ml-1 clear-btn"
            @click="clearOperator"
          >
            <i class="fas fa-times"></i>
          </b-button>
        </div>
      </b-form-group>

      <b-alert v-if="normalizedCode && countryBlocked === false" show variant="danger" class="mt-3">
        Country <b>{{ normalizedCode }}</b> is not blocked. Block the country first before adding exceptions.
      </b-alert>

      <div v-if="normalizedCode && countryBlockChecking" class="text-muted mt-3">
        <i class="fas fa-spinner fa-spin"></i> Checking country block status...
      </div>

      <hr v-if="normalizedCode && hasOperatorSelection && countryBlocked" />

      <h6 v-if="normalizedCode && hasOperatorSelection && countryBlocked">
        Exceptions for this operator
      </h6>
      <p v-if="!normalizedCode || !hasOperatorSelection" class="text-muted">
        Select a country and operator to configure exceptions.
      </p>
      <p v-else-if="countryBlocked" class="text-muted small">
        Changes are saved only when you click <b>Save</b>.
      </p>

      <CountryExceptionEditor
        v-if="editorReady && normalizedCode && hasOperatorSelection && countryBlocked"
        ref="exceptionEditor"
        :key="editorKey"
        :country-code="normalizedCode"
        :partner-id="editorPartnerId"
        :initial-exceptions="prefetchedExceptions"
      />

      <div v-if="normalizedCode && hasOperatorSelection && countryBlocked" class="mt-3 text-right">
        <b-button variant="primary" class="mr-2" :disabled="saving" @click="save">
          <i class="fas fa-save"></i> Save
        </b-button>
        <b-button variant="secondary" @click="modalShow = false">Close</b-button>
      </div>
    </b-modal>
  </span>
</template>

<script>
import Multiselect from "vue-multiselect";
import CountryCodeSelect from "./CountryCodeSelect.vue";
import CountryExceptionEditor from "./CountryExceptionEditor.vue";
import {
  fetchCountryExceptions,
  mergeExceptionSets,
  normalizePartnerId,
} from "./exceptionUtils";

export default {
  name: "ExceptionsManageForm",
  components: { Multiselect, CountryCodeSelect, CountryExceptionEditor },
  props: {
    title: { type: String, default: 'Manage exceptions' },
    initialCode: { type: String, default: '' },
    initialOperator: { type: Object, default: null },
    initialExceptions: { type: Object, default: null },
  },
  data() {
    return {
      modalShow: false,
      operatorsLoaded: false,
      operatorOptions: [],
      countryCode: '',
      operatorModel: null,
      saving: false,
      editorReady: false,
      prefetchedExceptions: null,
      countryBlocked: null,
      countryBlockChecking: false,
    };
  },
  computed: {
    normalizedCode() {
      const c = (this.countryCode || '').trim().toUpperCase();
      return c === '' ? null : c;
    },
    hasOperatorSelection() {
      return !!(this.operatorModel && this.operatorModel.value);
    },
    editorPartnerId() {
      return this.operatorModel?.value || null;
    },
    editorPartnerKey() {
      return this.operatorModel?.value || '';
    },
    editorKey() {
      return `${this.normalizedCode}|${this.editorPartnerKey}`;
    },
  },
  watch: {
    countryCode() {
      this.onSelectionChanged();
    },
  },
  methods: {
    toggle() {
      this.modalShow = !this.modalShow;
    },
    onHidden() {
      this.countryCode = '';
      this.operatorModel = null;
      this.editorReady = false;
      this.prefetchedExceptions = null;
      this.countryBlocked = null;
      this.countryBlockChecking = false;
    },
    onOperatorSelected() {
      this.onSelectionChanged();
    },
    clearOperator() {
      this.operatorModel = null;
      this.onSelectionChanged();
    },
    async checkCountryBlocked() {
      if (!this.normalizedCode) {
        this.countryBlocked = null;
        return;
      }

      this.countryBlockChecking = true;
      try {
        const resp = await this.$http({
          url: '/api/v1/country-blocks',
          method: 'GET',
          params: { code: this.normalizedCode, blocked: 'true' },
        });
        this.countryBlocked = resp.data.length > 0;
      } catch (err) {
        this.countryBlocked = false;
        this.$toastr.e('Failed to check country block status');
      } finally {
        this.countryBlockChecking = false;
      }
    },
    async onSelectionChanged() {
      this.editorReady = false;
      this.prefetchedExceptions = null;

      if (!this.normalizedCode) {
        this.countryBlocked = null;
        return;
      }

      await this.checkCountryBlocked();

      if (!this.hasOperatorSelection) {
        return;
      }

      if (!this.countryBlocked) {
        return;
      }

      if (this.operatorModel && this.operatorModel.value) {
        await this.loadOperatorExceptions(this.operatorModel.value);
        this.editorReady = true;
      }
    },
    async onShow() {
      this.editorReady = false;
      this.prefetchedExceptions = this.initialExceptions;
      this.countryCode = this.initialCode || '';
      this.operatorModel = null;

      await this.loadOperators();

      if (this.initialOperator && this.initialOperator.value) {
        const partnerId = normalizePartnerId(this.initialOperator.value);
        this.operatorModel = this.operatorOptions.find(o => o.value === partnerId)
          || { value: partnerId, label: this.initialOperator.label || partnerId };
      }

      if (this.normalizedCode && (this.hasOperatorSelection || (this.operatorModel && this.operatorModel.value))) {
        await this.onSelectionChanged();
      }
    },
    async loadOperators() {
      try {
        const resp = await this.$http({ url: '/api/v1/country-partners-for-select', method: 'GET' });
        this.operatorOptions = resp.data;
        this.operatorsLoaded = true;
      } catch (err) {
        this.$toastr.e('Failed to load operators');
      }
    },
    async loadOperatorExceptions(partnerId) {
      const fetched = await fetchCountryExceptions(
        (config) => this.$http(config),
        this.normalizedCode,
        partnerId,
      );
      this.prefetchedExceptions = mergeExceptionSets(fetched, this.initialExceptions);
    },
    getOperatorsToSave() {
      return this.operatorModel && this.operatorModel.value ? [this.operatorModel] : [];
    },
    async save() {
      if (!this.normalizedCode) {
        this.$toastr.w('Enter a country code first');
        return;
      }

      if (!this.countryBlocked) {
        this.$toastr.w('Country must be blocked before adding exceptions');
        return;
      }

      const operators = this.getOperatorsToSave();
      if (!operators.length) {
        this.$toastr.w('Select an operator first');
        return;
      }

      this.saving = true;
      try {
        const snapshot = this.$refs.exceptionEditor?.getSnapshot();
        const baseline = this.$refs.exceptionEditor?.getLoadedBaseline();

        if (snapshot) {
          await this.$refs.exceptionEditor.saveAll(baseline);
        }

        this.$toastr.i('Exceptions saved');
        this.$emit('finish');
        this.modalShow = false;
      } catch (err) {
        const message = err.response?.data?.message ?? err.message;
        this.$toastr.e(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.clear-btn {
  margin-top: 2px;
}
</style>
